import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { leadsApi, type Lead } from "@/api/leads";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const FIFTEEN_MS = 15 * 60 * 1000;
const DISMISS_PREFIX = "thms:crm:fu15-dismiss:";

function dismissedKey(leadId: string, followUpTs: number) {
  return `${DISMISS_PREFIX}${leadId}:${followUpTs}`;
}

function markDismissedForLeads(leads: Lead[]) {
  for (const l of leads) {
    if (!l.nextFollowUpAt) continue;
    const T = new Date(l.nextFollowUpAt).getTime();
    if (Number.isNaN(T)) continue;
    try {
      sessionStorage.setItem(dismissedKey(l.id, T), "1");
    } catch {
      /* ignore */
    }
  }
}

function getReminderLeads(leads: Lead[] | undefined, now: number): Lead[] {
  if (!leads?.length) return [];
  const out: Lead[] = [];
  for (const l of leads) {
    if (!l.nextFollowUpAt) continue;
    const T = new Date(l.nextFollowUpAt).getTime();
    if (Number.isNaN(T)) continue;
    const windowStart = T - FIFTEEN_MS;
    if (now < windowStart || now >= T) continue;
    try {
      if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(dismissedKey(l.id, T)))
        continue;
    } catch {
      /* ignore */
    }
    out.push(l);
  }
  return out;
}

function leadsListPathForRole(role: string | undefined): string {
  if (role === "SUPER_ADMIN") return "/super-admin/leads";
  if (role === "SALES_PERSON" || role === "SALES_MANAGER") return "/sales-person/leads";
  return "/hospital-admin/leads";
}

export default function LeadFollowUpReminderDialog() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const skipMarkOnCloseRef = useRef(false);
  /** After the user closes the modal, do not reopen until the reminder queue is empty (avoids refetch loops). */
  const userClosedWhilePendingRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [displayLeads, setDisplayLeads] = useState<Lead[]>([]);
  const [tick, setTick] = useState(0);

  const { data } = useQuery({
    queryKey: ["lead-followups-upcoming"],
    queryFn: async () => {
      const res = await leadsApi.getUpcomingFollowUps();
      return res.data.data as Lead[];
    },
    refetchInterval: 60 * 1000,
  });

  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const list = getReminderLeads(data, Date.now());
    if (list.length === 0) {
      userClosedWhilePendingRef.current = false;
      if (open) {
        skipMarkOnCloseRef.current = true;
        setOpen(false);
        setDisplayLeads([]);
      }
      return;
    }
    if (userClosedWhilePendingRef.current) return;
    if (open) {
      setDisplayLeads(list);
      return;
    }
    setDisplayLeads(list);
    setOpen(true);
  }, [data, tick, open]);

  const dismissAndClose = () => {
    markDismissedForLeads(displayLeads);
    userClosedWhilePendingRef.current = true;
    skipMarkOnCloseRef.current = true;
    setOpen(false);
    setDisplayLeads([]);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          if (!skipMarkOnCloseRef.current) markDismissedForLeads(displayLeads);
          userClosedWhilePendingRef.current = true;
          skipMarkOnCloseRef.current = false;
          setOpen(false);
          setDisplayLeads([]);
        } else {
          setOpen(true);
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Follow-up soon</DialogTitle>
          <p className="text-sm text-muted-foreground pt-1">
            The scheduled time is within the next 15 minutes.
          </p>
        </DialogHeader>
        <ul className="max-h-60 overflow-y-auto divide-y rounded-md border">
          {displayLeads.map((l) => (
            <li key={l.id} className="px-3 py-2 text-sm">
              <div className="font-medium">{l.patientName}</div>
              <div className="text-xs text-muted-foreground">
                {l.nextFollowUpAt ? new Date(l.nextFollowUpAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}
              </div>
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-xs"
                onClick={() => {
                  dismissAndClose();
                  const base = leadsListPathForRole(user?.role);
                  navigate(`${base}?openLeadId=${encodeURIComponent(l.id)}`);
                }}
              >
                Open lead
              </Button>
            </li>
          ))}
        </ul>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={dismissAndClose}>
            Dismiss
          </Button>
          <Button
            type="button"
            onClick={() => {
              dismissAndClose();
              navigate(leadsListPathForRole(user?.role));
            }}
          >
            Open Leads
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
